import { useCallback, useEffect, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import { ApiService, CompanyService, RetrieveDocumentGenerationJob } from '../../api';
import { USERS } from '../../common/endpoints';
import { useStore } from '../../common/store';
import { UseStoreValues } from '../../common/types/store';
import { EventType, useEventBus } from '../../context';
import { useAsyncProcessor } from '../../context/AsyncProcessorContext/AsyncProcessorContext';
import { clientWithAuth } from '../../dashboard409a/services/base';
import { Logger } from '../../utilities';
import CompaniesService from '../companies';

export enum POLL_TYPE {
  GENERATION_JOBS = 'generation_jobs',
}

const POLLING_INTERVAL_MS = 5000;

const TERMINAL_STATUSES = new Set([
  RetrieveDocumentGenerationJob.status.COMPLETED,
  RetrieveDocumentGenerationJob.status.FAILED,
]);

/**
 * Fetch the user's generation jobs from the user-scoped endpoint.
 * The backend syncs each job with the external service in this request,
 * so one call is sufficient to update statuses and fire notifications.
 *
 * Jobs that transition to a terminal state during the sync are still
 * returned (with the updated status) — they won't appear on the next call.
 */
const fetchActiveJobs = async (): Promise<RetrieveDocumentGenerationJob[]> => {
  const url = USERS.getUserGenerationJobs(true);
  const response = await clientWithAuth.get<RetrieveDocumentGenerationJob[]>(url);
  return response.data ?? [];
};

/**
 * Hook that polls all active generation jobs for the authenticated user.
 *
 * Serves two purposes:
 * 1. Polling loop via `GET /api/users/me/document-generation-jobs?active_only=true`:
 *    triggers backend status sync → Ably publishes updates → notification center picks them up.
 *    Terminal side effects for FINANCIAL_MAPPING (attribute mapping creation) are handled by
 *    the Ably `onJobCompleted` callback in Main — not here.
 *
 * 2. AsyncProcessorContext per-job registration: initializes per-job polling for active
 *    FINANCIAL_MAPPING and CAP_TABLE jobs on the current company, enabling UI progress tracking.
 *    CAP_TABLE side effects (applying results to the cap table) are handled here via `onResult`.
 */
export const useAppLoadAsyncOperations = (initOnLoad = true) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { addJob, removeJob } = useAsyncProcessor();
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo } = storeValue;
  const { publish } = useEventBus();

  // ── Ably-trigger polling loop ─────────────────────────────────────────────
  // Polls the user-scoped endpoint to trigger backend sync. Ably then fires
  // status-change events that the notification center consumes.

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async (): Promise<boolean> => {
    try {
      const jobs = await fetchActiveJobs();
      return jobs.some(j => !TERMINAL_STATUSES.has(j.status!));
    } catch (error) {
      Logger.error('useAppLoadAsyncActions - Polling error', error);
      return false;
    }
  }, []);

  const startPolling = useCallback(async () => {
    if (intervalRef.current) {
      return;
    }

    const hasActive = await pollOnce();
    if (!hasActive) {
      return;
    }

    Logger.info('useAppLoadAsyncActions:', 'Active generation jobs found, starting poll');
    intervalRef.current = setInterval(async () => {
      const stillActive = await pollOnce();
      if (!stillActive) {
        stopPolling();
      }
    }, POLLING_INTERVAL_MS);
  }, [pollOnce, stopPolling]);

  // ── AsyncProcessorContext per-job registration ────────────────────────────
  // Registers active FINANCIAL_MAPPING and CAP_TABLE jobs with AsyncProcessorContext
  // for individual polling and UI progress tracking.
  // FINANCIAL_MAPPING side effects (attribute mapping creation) are intentionally
  // omitted here — handled by the Ably onJobCompleted callback in Main.

  const initializeGenerationJobs = useCallback(async () => {
    const companiesService = new CompaniesService();
    try {
      if (!companyInfo?.id) {
        return;
      }

      const companyIdStr = companyInfo.id!.toString();

      // TODO: update the API here to enable OR searching...
      // Fetch FINANCIAL_MAPPING and CAP_TABLE jobs with PENDING or IN_PROGRESS status.
      // Multiple calls because the API doesn't support OR conditions for status.
      const [
        pendingFinMapJobs,
        inProgressFinMapJobs,
        pendingCapTableJobs,
        inProgressCapTableJobs,
      ] = await Promise.all([
        companiesService.getDocumentGenerationJobs(
          companyIdStr,
          undefined,
          undefined,
          RetrieveDocumentGenerationJob.status.PENDING,
          RetrieveDocumentGenerationJob.processing_type.FINANCIAL_MAPPING
        ),
        companiesService.getDocumentGenerationJobs(
          companyIdStr,
          undefined,
          undefined,
          RetrieveDocumentGenerationJob.status.IN_PROGRESS,
          RetrieveDocumentGenerationJob.processing_type.FINANCIAL_MAPPING
        ),
        companiesService.getDocumentGenerationJobs(
          companyIdStr,
          undefined,
          undefined,
          RetrieveDocumentGenerationJob.status.PENDING,
          RetrieveDocumentGenerationJob.processing_type.CAP_TABLE
        ),
        companiesService.getDocumentGenerationJobs(
          companyIdStr,
          undefined,
          undefined,
          RetrieveDocumentGenerationJob.status.IN_PROGRESS,
          RetrieveDocumentGenerationJob.processing_type.CAP_TABLE
        ),
      ]) as [
        RetrieveDocumentGenerationJob[],
        RetrieveDocumentGenerationJob[],
        RetrieveDocumentGenerationJob[],
        RetrieveDocumentGenerationJob[],
      ];

      const activeFinMapJobs = [...pendingFinMapJobs, ...inProgressFinMapJobs];
      const activeCapTableJobs = [...pendingCapTableJobs, ...inProgressCapTableJobs];

      if (activeFinMapJobs.length > 0) {
        Logger.info('useAppLoadAsyncActions:', 'Found active FINANCIAL_MAPPING jobs to poll', activeFinMapJobs.length);
      }
      if (activeCapTableJobs.length > 0) {
        Logger.info('useAppLoadAsyncActions:', 'Found active CAP_TABLE jobs to poll', activeCapTableJobs.length);
      }

      // Register FINANCIAL_MAPPING jobs for individual polling.
      // Mapping creation side effect is handled by the Ably onJobCompleted callback in Main.
      activeFinMapJobs.forEach((job: RetrieveDocumentGenerationJob) => {
        addJob({
          id: job.job_id,
          details: {
            title: 'Financial Mapping',
            description: `Processing job ${job.job_id}`,
          },
          pollingConfig: {
            pollingFunction: async () => {
              const result = await CompanyService.apiCompanyDocumentGenerationJobsRead(
                companyIdStr,
                job.job_id
              );
              return result;
            },
            pollingInterval: 5000,
            pollingArgs: [],
          },
          onResult: async (result: RetrieveDocumentGenerationJob) => {
            if (TERMINAL_STATUSES.has(result.status!)) {
              removeJob(result.job_id);
            }
          },
          onError: (error: Error) => {
            Logger.error('useAppLoadAsyncActions - Generation job polling error', error);
          },
        });
      });

      // Register CAP_TABLE jobs — side effects handled here (not yet via Ably).
      activeCapTableJobs.forEach((job: RetrieveDocumentGenerationJob) => {
        addJob({
          id: job.job_id,
          details: {
            title: 'Generating Cap Table',
            description: `Processing job ${job.job_id}`,
          },
          pollingConfig: {
            pollingFunction: async () => {
              const result = await CompanyService.apiCompanyDocumentGenerationJobsRead(
                companyIdStr,
                job.job_id
              );
              return result;
            },
            pollingInterval: 3000,
            pollingArgs: [],
          },
          onResult: async (result: RetrieveDocumentGenerationJob) => {
            if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
              try {
                const applyResult = await ApiService.apiCompanyCapTablesApplyGenerationJobCreate(companyIdStr, {
                  job_id: result.job_id,
                });

                const count = applyResult?.security_ids?.length ?? 0;
                publish(EventType.CAP_TABLE_GENERATION_JOB_COMPLETED, result.job_id);
                enqueueSnackbar(
                  count > 0
                    ? `Cap table updated with ${count} securities from AI results`
                    : 'Cap table generation completed',
                  { variant: 'success' }
                );
              } catch (applyError) {
                Logger.error('useAppLoadAsyncActions - Error applying cap table generation job', applyError);
                enqueueSnackbar('Job completed but applying results to the cap table failed', {
                  variant: 'warning',
                });
              }
              removeJob(result.job_id);
            } else if (result.status === RetrieveDocumentGenerationJob.status.FAILED) {
              enqueueSnackbar('Cap table generation job failed', { variant: 'error' });
              removeJob(result.job_id);
            }
          },
          onError: (error: Error) => {
            Logger.error('useAppLoadAsyncActions - Cap table generation job polling error', error);
          },
        });
      });
    } catch (error) {
      Logger.error('useAppLoadAsyncActions - Error initializing jobs from API', error);
    }
    // companiesService is not used in the dependencies because it is a new instance on each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo, addJob, removeJob, publish]);

  const manuallyExecutePoll = useCallback(
    (pollType: POLL_TYPE.GENERATION_JOBS) => {
      if (pollType === POLL_TYPE.GENERATION_JOBS) {
        startPolling();
        initializeGenerationJobs();
      }
    },
    [startPolling, initializeGenerationJobs]
  );

  useEffect(() => {
    if (initOnLoad) {
      startPolling();
      initializeGenerationJobs();
    }
    return stopPolling;
  }, [initOnLoad, startPolling, stopPolling, initializeGenerationJobs]);

  return { manuallyExecutePoll };
};

export default useAppLoadAsyncOperations;
